import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   * @param {boolean} force - If true, disable regardless of variant param (for unavailable/sold out variants)
   */
  disable(force = false) {
    // If force is true, always disable (variant/product is sold out)
    if (force) {
      this.refs.addToCartButton.disabled = true;
      this.refs.addToCartButton.setAttribute('aria-disabled', 'true');
      return;
    }
    
    // If force is false, don't disable if we have a variant param in URL
    // This prevents unnecessary disabling when navigating with variant param
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    if (urlVariantId) {
      // Don't disable - keep button enabled when navigating with variant param
      return;
    }
    
    // No variant param and force is false - don't disable
    return;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
    this.refs.addToCartButton.removeAttribute('aria-disabled');
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;
  /** @type {boolean} */
  #didRunInitialCheck = false;
  /** @type {Promise<any> | null} */
  static #productJsonPromise = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // If we have a variant param in URL, check availability first before enabling
    // The availability check will handle enabling/disabling based on actual availability
    this.runInitialAvailabilityCheck();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const simpleBundlePicker = this.closest('.shopify-section')?.querySelector('sunice-simple-bundle-picker');
    if (simpleBundlePicker?.validateSelection?.(form) === false) return;

    const formData = new FormData(form);
    if (simpleBundlePicker?.writeProperties?.(formData, form) === false) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          const errorCartAddEvent = new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
          });
          
          // Dispatch on the component (for other listeners)
          this.dispatchEvent(errorCartAddEvent);
          
          // Also dispatch on document to ensure cart drawer receives it
          document.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          const cartAddEvent = new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections,
          });
          
          // Dispatch on the component (for other listeners)
          this.dispatchEvent(cartAddEvent);
          
          // Also dispatch on document to ensure cart drawer receives it
          // Create a new event instance since we can't dispatch the same event twice
          document.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    if (!addToCartButtonContainer?.refs.addToCartButton) return;

    // Update the button state - ALWAYS enable unless variant is actually unavailable
    // If resource exists, use its availability status
    if (event.detail.resource) {
      if (event.detail.resource.available === false) {
        // Only disable if variant is actually unavailable
        addToCartButtonContainer.disable(true);
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        // Variant is available - ALWAYS enable the button
        addToCartButtonContainer.enable();
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    } else {
      // If resource is null, check URL variant or enable by default if we have a variant ID
      // This handles cases where variant update fires but resource isn't populated yet
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');
      const currentVariantId = variantId?.value || urlVariantId;
      
      if (currentVariantId && ProductFormComponent.#productJsonPromise) {
        // Check if the current variant is available
        ProductFormComponent.#productJsonPromise.then((productJson) => {
          if (productJson && Array.isArray(productJson.variants)) {
            const variant = productJson.variants.find((v) => String(v.id) === String(currentVariantId));
            if (variant && variant.available) {
              // Variant is available - FORCE enable (defensive against race conditions)
              addToCartButtonContainer.enable();
              this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
            } else if (variant && variant.available === false) {
              // Only disable if variant is actually unavailable
              addToCartButtonContainer.disable(true);
              this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
            } else {
              // Variant not found or status unknown - enable by default
              addToCartButtonContainer.enable();
              this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
            }
          }
        }).catch(() => {
          // On error, if we have a variant ID, enable by default (defensive)
          if (currentVariantId) {
            addToCartButtonContainer.enable();
            this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
          }
        });
      } else if (currentVariantId) {
        // We have a variant ID but no product JSON yet - enable by default
        // The initial check will correct this if needed
        addToCartButtonContainer.enable();
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }
    
    // DEFENSIVE: If we have a variant param in URL and button is disabled, 
    // check if it should be enabled (only if variant is available)
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    if (urlVariantId && addToCartButtonContainer?.refs.addToCartButton?.disabled) {
      // Button is disabled but we have variant param - check if variant is available
      if (ProductFormComponent.#productJsonPromise) {
        ProductFormComponent.#productJsonPromise.then((productJson) => {
          if (productJson && Array.isArray(productJson.variants)) {
            const variant = productJson.variants.find((v) => String(v.id) === String(urlVariantId));
            if (variant && variant.available) {
              // Variant is available - enable it
              addToCartButtonContainer.enable();
              this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
            }
            // If variant is unavailable, keep it disabled (no action needed)
          }
        }).catch(() => {
          // On error, don't change state - keep as is
        });
      }
    }

    // Do not morph/change button text (e.g. to "Sold out") - only toggle disabled state above

    // Update the variant ID
    if (variantId && event.detail.resource?.id != null) {
      variantId.value = String(event.detail.resource.id);
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Handle variant selected event - DO NOT disable the button
   * Just ensure it's enabled if it's currently disabled
   */
  #onVariantSelected = (event) => {
    // NEVER disable the button - always keep it enabled
    // If button is disabled, enable it
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (button?.disabled) {
      this.refs.addToCartButtonContainer?.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }
  };

  /**
   * Disable/enable ATC on initial page load based on current variant availability
   * and overall product availability.
   * Only disable if ALL variants are unavailable - don't disable if product has any available variants.
   */
  runInitialAvailabilityCheck() {
    const bundlePicker = this.closest('.shopify-section')?.querySelector('sunice-bundle-picker');
    if (bundlePicker?.dataset.productId === this.dataset.productId) return;
    if (this.#didRunInitialCheck) return;
    this.#didRunInitialCheck = true;

    const path = window.location?.pathname || '';
    // Only run on PDP URLs
    if (!/\/products\//.test(path)) return;

    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');
    const currentVariantId = (urlVariantId || this.refs.variantId?.value || '').trim();

    if (!ProductFormComponent.#productJsonPromise) {
      const productJsonUrl = path.replace(/\/$/, '') + '.js';
      ProductFormComponent.#productJsonPromise = fetch(productJsonUrl)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to fetch product JSON'))))
        .catch(() => null);
    }

    ProductFormComponent.#productJsonPromise.then((productJson) => {
      if (!productJson || !Array.isArray(productJson.variants)) return;

      const anyAvailable = productJson.variants.some((v) => !!v.available);
      // Only disable if ALL variants are unavailable
      if (!anyAvailable) {
        // Only disable if ALL variants are actually unavailable
        this.refs.addToCartButtonContainer?.disable(true);
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        return;
      }
      
      // Product has available variants - ALWAYS enable the button
      // Check if URL variant is available, but default to enabled
      if (currentVariantId) {
        const variant = productJson.variants.find((v) => String(v.id) === String(currentVariantId));
        if (variant && variant.available === false) {
          // Only disable if this specific variant is unavailable
          this.refs.addToCartButtonContainer?.disable(true);
          this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          // Variant is available OR not found - ALWAYS enable button
          this.refs.addToCartButtonContainer?.enable();
          this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      } else {
        // No variant in URL - enable button (default state)
        this.refs.addToCartButtonContainer?.enable();
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }).catch((error) => {
      // On error, if we have a variant param, enable by default (defensive)
      const urlParams = new URLSearchParams(window.location.search);
      const urlVariantId = urlParams.get('variant');
      if (urlVariantId) {
        this.refs.addToCartButtonContainer?.enable();
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    });
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
